/**
 * EmployeeProfilePage — the professional Growth/CV profile page of one Digital Employee
 * (DIGEMP-GROWTH-CV-V2-FRONTEND-086; professional identity header added by
 * DIGEMP-PRODUCTIZATION-GROWTH-UX-112).
 *
 * Route: /agents/:id/cv
 * One read-only operator profile: the professional identity header (role, tenure, persona
 * tagline), a CV cover with the declared-specialty line apart from the evaluated-proficiency
 * line, a CV summary plus drill-down to audited experience, verified outcomes with significant
 * work, declared-vs-measured skills, learning goals and the policy envelope (known limits +
 * approved tool qualifications).
 *
 * Access: the same agents_view gate as /agents/:id — every source this page reads (agent detail,
 * the growth profile aggregate, the audited experience history, the run window and the workspace
 * entitlement projection) already enforces that gate server-side; this page adds no data source
 * and weakens no gate. Nothing here is writable: no action, approval or entitlement control is
 * presented, and no raw payload or credential material is rendered.
 */
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, agentRunsApi } from "../services/api";
import EmployeeAvatar, { STATUS_META, drawnStatus } from "../components/EmployeeAvatar";
import EmployeeProfileView from "../components/EmployeeProfileView";

// The audited run window the experience timeline drill-down renders (same bounded window the
// employee workspace uses for its experience view).
export const PROFILE_RUNS_LIMIT = 50;

const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);
const boundedText = (value, max) => {
  const trimmed = text(value);
  return trimmed ? trimmed.slice(0, max) : null;
};

function fmtMonth(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("he-IL", { year: "numeric", month: "long" });
}

export default function EmployeeProfilePage() {
  const { id } = useParams();

  const { data: agent, isLoading: agentLoading, isError: agentError, error: agentErrorObj } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });

  const {
    data: profile = null,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ["agent-growth-profile", id],
    queryFn: () => agentsApi.growthProfile(id),
    enabled: !!id,
  });

  const {
    data: experienceHistory = null,
    isLoading: experienceLoading,
    isError: experienceError,
    refetch: refetchExperience,
  } = useQuery({
    queryKey: ["agent-experience-history", id],
    queryFn: () => agentsApi.experienceHistory(id),
    enabled: !!id,
  });

  const {
    data: workspace = null,
    isLoading: workspaceLoading,
    isError: workspaceError,
    refetch: refetchWorkspace,
  } = useQuery({
    queryKey: ["agent-workspace", id],
    queryFn: () => agentsApi.workspace(id),
    enabled: !!id,
  });

  const { data: runs = [], isLoading: runsLoading, isError: runsError } = useQuery({
    queryKey: ["agentRuns", "agent", id, "profile"],
    queryFn: () => agentRunsApi.listByAgent(id, { limit: PROFILE_RUNS_LIMIT }),
    enabled: !!id,
  });

  if (agentLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!agent) {
    const forbidden = agentErrorObj?.response?.status === 403;
    return (
      <div className="text-center py-16 space-y-2" dir="rtl">
        <p className="text-red-600 text-sm">
          {forbidden || agentError
            ? "לא ניתן להציג את הפרופיל המקצועי של העובד (אין הרשאה או שהעובד לא נמצא)"
            : "לא ניתן לטעון את העובד"}
        </p>
        <Link to="/agents" className="text-xs text-brand-600 hover:underline">← חזרה לעובדים</Link>
      </div>
    );
  }

  // The same rule the avatar applies to its dot, read from the one place that owns it: this page
  // prints the status as text as well, and a disabled employee must not read "זמין" one line above
  // its own "מושבת" badge.
  const statusMeta = STATUS_META[drawnStatus(agent.availability_status, agent.is_enabled)];
  // The professional identity of the employee: since when it has been working (the agent record's
  // own creation time) and its own persona tagline. Both are optional — absent values render
  // nothing rather than an invented statement.
  const memberSince = fmtMonth(agent.created_at);
  const tagline = boundedText(agent.intro_text, 240);

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link to="/agents" className="hover:text-gray-600">עובדים</Link>
            <span>/</span>
            <Link to={`/agents/${id}`} className="hover:text-gray-600">{agent.name}</Link>
            <span>/</span>
            <span className="text-gray-600">פרופיל מקצועי · CV</span>
          </div>
          <div className="flex items-center gap-3">
            <EmployeeAvatar
              name={agent.name}
              avatarKey={agent.avatar_key}
              avatarColor={agent.avatar_color}
              status={agent.availability_status}
              isEnabled={agent.is_enabled}
              size="lg"
              showStatus
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 flex-wrap">
                {agent.name}
                <span className={`text-sm font-normal px-2 py-0.5 rounded-full ${
                  agent.is_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {agent.is_enabled ? "פעיל" : "מושבת"}
                </span>
                {statusMeta && <span className="text-xs font-normal text-gray-500">· {statusMeta.label}</span>}
              </h1>
              {agent.role_title && (
                <p className="text-sm font-medium text-violet-700">{agent.role_title}</p>
              )}
              {(memberSince || tagline) && (
                <div className="mt-0.5 space-y-1">
                  {memberSince && <p className="text-xs text-gray-500">עובד מאז {memberSince}</p>}
                  {tagline && (
                    <p className="text-sm text-gray-600 italic border-r-2 border-violet-200 pr-2 max-w-xl">
                      “{tagline}”
                    </p>
                  )}
                </div>
              )}
              {agent.model && (
                <p className="text-[11px] text-gray-400 mt-1">
                  פרטי תפעול · מודל: <span className="font-mono" dir="ltr">{agent.model}</span>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Link
            to={`/agents/${id}`}
            className="text-sm px-3 py-2 rounded-lg border hover:bg-gray-50"
          >
            לדף העובד
          </Link>
          <Link
            to="/agent-runs"
            className="text-sm px-3 py-2 rounded-lg border hover:bg-gray-50"
          >
            ריצות AI
          </Link>
        </div>
      </div>

      <EmployeeProfileView
        agent={agent}
        profile={profile}
        profileLoading={profileLoading}
        profileError={profileError}
        onRetryProfile={() => refetchProfile()}
        experienceHistory={experienceHistory}
        experienceLoading={experienceLoading}
        experienceError={experienceError}
        onRetryExperience={() => refetchExperience()}
        workspace={workspace}
        workspaceLoading={workspaceLoading}
        workspaceError={workspaceError}
        onRetryWorkspace={() => refetchWorkspace()}
        runs={runs}
        runsLoading={runsLoading}
        runsError={runsError}
      />
    </div>
  );
}
